class TheFoldProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    this.variantSelect = this.querySelector('[data-variant-select]');
    this.submitButton = this.querySelector('[data-submit]');
    this.submitLabel = this.submitButton?.querySelector('[data-submit-label]');
    this.status = this.querySelector('[data-status]');
    this.prices = this.parentElement.querySelectorAll('[data-price]');
    this.comparePrices = this.parentElement.querySelectorAll('[data-compare-price]');
    this.gallery = this.querySelector('[data-kind="gallery"]');
    this.sticky = this.parentElement.querySelector('[data-sticky]');
    this.stickyButton = this.sticky?.querySelector('[data-sticky-submit]');
    this.stickyStatus = this.sticky?.querySelector('[data-sticky-status]');
    this.stickyLabel = this.stickyButton?.querySelector('[data-submit-label]');
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.abortController = new AbortController();
    this.pendingForms = new Set();

    const signal = this.abortController.signal;
    // Own the submission before cart apps intercept it with a pre-purchase offer.
    this.addEventListener('submit', (event) => this.addToCart(event), { signal, capture: true });
    this.addEventListener('click', (event) => this.handleCartClick(event), { signal, capture: true });
    this.sticky?.addEventListener('click', (event) => this.handleCartClick(event), { signal, capture: true });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('a[href="#TheFold-main"]')) return;
      event.preventDefault();
      this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    this.variantSelect?.addEventListener('change', () => this.updateVariant(), { signal });
    const placePaymentMessage = () => {
      const message = this.querySelector('#paypal-upstream-messaging-product');
      const price = this.querySelector('[data-payment-message-container]');
      if (message && price && message.parentElement !== price) price.querySelector('[data-price]').after(message);
    };
    this.paymentObserver = new MutationObserver(placePaymentMessage);
    this.paymentObserver.observe(this, { childList: true, subtree: true });
    placePaymentMessage();

    if (this.sticky) {
      const updateStickyAccess = () => {
        cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = requestAnimationFrame(() => {
          const bar = this.sticky.getBoundingClientRect();
          const coveringSections = [this, ...document.querySelectorAll('.the-fold-section--ticker, .the-fold-accordion--details')];
          const covered = coveringSections.some((section) => {
            const box = section.getBoundingClientRect();
            return box.top < bar.bottom && box.bottom > bar.top + .5;
          });
          this.sticky.inert = covered;
          this.sticky.setAttribute('aria-hidden', String(covered));
        });
      };
      document.addEventListener('scroll', updateStickyAccess, { signal, passive: true });
      window.addEventListener('resize', updateStickyAccess, { signal });
      this.stickyObserver = new ResizeObserver(updateStickyAccess);
      this.stickyObserver.observe(this.sticky);
      document.querySelectorAll('.the-fold-accordion--details').forEach((section) => this.stickyObserver.observe(section));
      updateStickyAccess();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stickyObserver?.disconnect();
    cancelAnimationFrame(this.stickyFrame);
    this.paymentObserver?.disconnect();
  }

  updateVariant() {
    const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
    if (!variant) return;
    this.submitButton.disabled = !variant.available;
    if (this.stickyButton) this.stickyButton.disabled = !variant.available;
    const label = variant.available ? this.dataset.addLabel : this.dataset.soldLabel;
    if (this.submitLabel) this.submitLabel.textContent = label;
    if (this.stickyLabel) this.stickyLabel.textContent = label;
    if (this.stickyButton) this.stickyButton.setAttribute('aria-label', label);
    for (const price of this.prices) price.textContent = this.formatMoney(variant.price);
    for (const comparePrice of this.comparePrices) {
      comparePrice.hidden = !(variant.compare_at_price > variant.price);
      comparePrice.textContent = this.formatMoney(variant.compare_at_price || 0);
    }
    const saving = this.querySelector('[data-saving]');
    if (saving) {
      saving.hidden = !(variant.compare_at_price > variant.price);
      saving.querySelector('[data-saved-amount]').textContent = this.formatMoney((variant.compare_at_price || 0) - variant.price);
      saving.querySelector('[data-saved-percent]').textContent = `${Math.floor(((variant.compare_at_price || 0) - variant.price) * 100 / (variant.compare_at_price || 1))}%`;
    }
    const message = this.querySelector('[data-pp-message]');
    if (message) message.dataset.ppAmount = String(variant.price / 100);
    const mediaIndex = this.gallery?.slides?.findIndex((slide) => String(variant.featured_media?.id) === slide.dataset.mediaId);
    if (mediaIndex >= 0) this.gallery.goTo(mediaIndex);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'GBP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  }

  handleCartClick(event) {
    const button = event.target.closest('button');
    if (!button) return;
    const form = button === this.stickyButton ? this.form : button.form;
    if (!form?.matches('[data-the-fold-cart]') || (button !== this.stickyButton && button.type !== 'submit')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!button.disabled) {
      this.requestedFromSticky = button === this.stickyButton;
      form.requestSubmit();
    }
  }

  async addToCart(event) {
    const form = event.target;
    if (!form.matches('[data-the-fold-cart]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const fromSticky = this.requestedFromSticky;
    this.requestedFromSticky = false;
    const button = form.querySelector('[type="submit"]');
    if (!button || button.disabled || this.pendingForms.has(form)) return;
    const isMainForm = form === this.form;
    const status = isMainForm ? (fromSticky ? this.stickyStatus : this.status) : this.querySelector('[data-recommendation-status]');
    const buttons = [button, ...(isMainForm && this.stickyButton ? [this.stickyButton] : [])];
    if (status) status.textContent = '';
    if (isMainForm) {
      if (this.status) this.status.textContent = '';
      if (this.stickyStatus) this.stickyStatus.textContent = '';
    }
    this.pendingForms.add(form);
    for (const control of buttons) {
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) {
        if (status) status.textContent = result.description || this.dataset.errorLabel;
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: result }));
      if (typeof window.AMP_API?.OPEN_CART === 'function') {
        document.dispatchEvent(new CustomEvent('instant:add-to-cart', { detail: { useInstantCart: false } }));
      } else if (typeof window.ajaxCart?.load === 'function') {
        window.ajaxCart.load();
      } else {
        window.location.assign(this.dataset.cartUrl);
      }
    } catch {
      if (status) status.textContent = this.dataset.errorLabel;
    } finally {
      this.pendingForms.delete(form);
      const variant = isMainForm ? this.variants.find((item) => String(item.id) === this.variantSelect?.value) : null;
      for (const control of buttons) {
        control.disabled = variant?.available === false;
        control.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('the-fold-product')) customElements.define('the-fold-product', TheFoldProduct);

if (!window.theFoldVideoControlsBound) {
  window.theFoldVideoControlsBound = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-the-fold-video-play]');
    if (!button) return;
    const video = button.parentElement.querySelector('video');
    if (!video) return;
    video.controls = true;
    video.play();
    button.hidden = true;
  });
}

if (!window.theFoldStoryControlsBound) {
  window.theFoldStoryControlsBound = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-the-fold-stories-prev], [data-the-fold-stories-next]');
    if (!button) return;
    const track = button.closest('.the-fold-section--stories')?.querySelector('.the-fold-section__grid');
    const card = track?.querySelector('.the-fold-card');
    if (!track || !card) return;
    const direction = button.matches('[data-the-fold-stories-prev]') ? -1 : 1;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  });
}
